import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ChevronDown, ShieldCheck, Zap, Download, Lock, Star, BookOpen, TrendingUp, Users, Globe, Award, Menu, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { SocialProofPopup } from './components/SocialProofPopup';
import { MetaPixel } from './components/MetaPixel';
import { ScrollButton } from './components/ScrollButton';
const FadeIn = ({
  children,
  delay = 0
}: {
  children: React.ReactNode;
  delay?: number;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-80px'
}} transition={{
  duration: 0.6,
  delay
}} data-id="element-0">
    {children}
  </motion.div>;
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollTo = (id: string) => {
    setMobileOpen(false);
    document.getElementById(id)?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  return <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-navy-900/95 backdrop-blur-md shadow-lg' : 'bg-navy-900/80 backdrop-blur-sm'}`} data-id="element-1">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-2">
        <div className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({
        top: 0,
        behavior: 'smooth'
      })} data-id="element-3">
          <span className="text-xl" data-id="element-4">📋</span>
          <span className="font-heading font-bold text-white text-sm md:text-base" data-id="element-5">
            Micro & Macro Economía 360
          </span>
        </div>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6" data-id="element-6">
          <button onClick={() => scrollTo('bonos')} className="text-slate-300 hover:text-white text-sm font-medium transition-colors" data-id="element-7">
            Bonos
          </button>
          <button onClick={() => scrollTo('testimonios')} className="text-slate-300 hover:text-white text-sm font-medium transition-colors" data-id="element-8">
            Testimonios
          </button>
          <button onClick={() => scrollTo('compra')} className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold text-sm py-2 px-5 rounded-full transition-all hover:-translate-y-0.5 shadow-[0_0_15px_rgba(212,160,23,0.3)]" data-id="element-9">
            COMPRAR AHORA
          </button>
        </div>

        {/* Mobile hamburger */}
        <button onClick={() => setMobileOpen(!mobileOpen)} className="md:hidden text-white p-1" data-id="element-10">
          {mobileOpen ? <X className="w-6 h-6" data-id="element-11" /> : <Menu className="w-6 h-6" data-id="element-12" />}
        </button>
      </div>

      {/* Mobile menu */}
      <AnimatePresence data-id="element-13">
        {mobileOpen && <motion.div initial={{
        opacity: 0,
        height: 0
      }} animate={{
        opacity: 1,
        height: 'auto'
      }} exit={{
        opacity: 0,
        height: 0
      }} className="md:hidden bg-navy-900/95 backdrop-blur-md border-t border-navy-700 overflow-hidden" data-id="element-14">
            <div className="px-4 py-4 flex flex-col gap-3" data-id="element-15">
              <button onClick={() => scrollTo('bonos')} className="text-slate-300 hover:text-white text-sm font-medium text-left py-2" data-id="element-16">
                Bonos
              </button>
              <button onClick={() => scrollTo('testimonios')} className="text-slate-300 hover:text-white text-sm font-medium text-left py-2" data-id="element-17">
                Testimonios
              </button>
              <button onClick={() => scrollTo('compra')} className="bg-gold-500 hover:bg-gold-400 text-navy-900 font-bold text-sm py-3 px-5 rounded-full transition-all text-center" data-id="element-18">
                COMPRAR AHORA
              </button>
            </div>
          </motion.div>}
      </AnimatePresence>
    </nav>;
}
export function App() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [testimonialPage, setTestimonialPage] = useState(0);
  const [selectedPlan, setSelectedPlan] = useState<'starter' | 'completo' | 'full360'>('completo');
  const tomos = [{
    id: 1,
    icon: <BookOpen className="w-7 h-7 text-blue-400" data-id="element-19" />,
    color: 'bg-blue-900/50 border-blue-500/30',
    title: 'TOMO 1 — Fundamentos de Microeconomía',
    subtitle: '"Del aula a la heladera: cómo piensan las personas y las empresas"',
    topics: 'Necesidades, bienes y escasez · Oferta, demanda y precio de equilibrio · Elasticidades · Costos de producción · Estructuras de mercado',
    activities: '✏️ 12 actividades progresivas · 📊 3 proyectos prácticos · 📝 Resolucionario completo con criterios de corrección'
  }, {
    id: 2,
    icon: <TrendingUp className="w-7 h-7 text-orange-400" data-id="element-20" />,
    color: 'bg-orange-900/50 border-orange-500/30',
    title: 'TOMO 2 — Microeconomía Aplicada Argentina',
    subtitle: '"La economía que los chicos viven todos los días"',
    topics: 'El consumidor argentino · PyMEs: estructura y costos reales · Competencia en mercados locales · Regulaciones y precios controlados · El rol del Estado',
    activities: '✏️ 10 actividades con casos reales · 📊 2 proyectos: "Mi negocio del barrio" + "Análisis de góndola" · 📝 Resolucionario con respuestas modelo y rúbricas'
  }, {
    id: 3,
    icon: <Globe className="w-7 h-7 text-green-400" data-id="element-21" />,
    color: 'bg-green-900/50 border-green-500/30',
    title: 'TOMO 3 — Fundamentos de Macroeconomía',
    subtitle: '"Cómo funciona la economía del país entero"',
    topics: 'PBI, crecimiento y desarrollo · Empleo e informalidad · Inflación: causas y consecuencias · Sistema financiero · Política fiscal y presupuesto',
    activities: '✏️ 12 actividades con datos del INDEC adaptados · 📊 3 proyectos: "Midiendo la economía" + "La inflación en mi casa" · 📝 Resolucionario con gráficos resueltos'
  }, {
    id: 4,
    icon: <TrendingUp className="w-7 h-7 text-red-400" data-id="element-22" />,
    color: 'bg-red-900/50 border-red-500/30',
    title: 'TOMO 4 — Macroeconomía Aplicada Argentina',
    subtitle: '"El dólar, la deuda y la política económica explicados en serio"',
    topics: 'Tipo de cambio y mercado del dólar · Deuda externa · Modelos económicos argentinos · Balanza de pagos · Crisis económicas',
    activities: '✏️ 10 actividades de análisis histórico-económico · 📊 2 proyectos: "Línea de tiempo económica" + "Debate: ¿qué modelo funciona?" · 📝 Resolucionario con guía de debate'
  }, {
    id: 5,
    icon: <Users className="w-7 h-7 text-purple-400" data-id="element-23" />,
    color: 'bg-purple-900/50 border-purple-500/30',
    title: 'TOMO 5 — Integración Micro + Macro',
    subtitle: '"Cuando todo se conecta: del individuo al país"',
    topics: 'Decisiones micro y su impacto macro · Sectores productivos y PBI · Distribución del ingreso · Globalización · Desarrollo sustentable',
    activities: '✏️ 8 actividades integradoras · 📊 3 proyectos transversales · 📝 Resolucionario con conexiones explícitas entre ambas disciplinas'
  }, {
    id: 6,
    icon: <Award className="w-7 h-7 text-gold-400" data-id="element-24" />,
    color: 'bg-yellow-900/50 border-gold-500/30',
    title: 'TOMO 6 — Evaluaciones, Proyectos Finales y Cierre Anual',
    subtitle: '"Todo listo para cerrar el año con impacto"',
    topics: 'Banco de evaluaciones bimestrales/trimestrales · Rúbricas por competencias · Trabajos prácticos integradores · Proyecto final anual · Instrumentos de autoevaluación',
    activities: '✏️ 20 evaluaciones listas · 📊 Mega-proyecto anual con guía paso a paso · 📝 Resolucionario y criterios de corrección para cada instrumento'
  }];
  const testimonials = [{
    text: 'Después de 15 años dando economía, este material cambió completamente mi forma de enseñar. Mis alumnos ahora participan y entienden. Ya no improviso más.',
    author: 'María Fernanda López, 42 años',
    location: 'Buenos Aires'
  }, {
    text: 'Siempre armaba las evaluaciones el domingo a la noche. Con el Tomo 6 y las rúbricas ya incluidas, ese problema desapareció. Una inversión que se paga sola.',
    author: 'Rodrigo Sánchez, 38 años',
    location: 'Córdoba'
  }, {
    text: 'Los ejemplos con el dólar y la inflación argentina son exactamente lo que necesitaba. Los chicos preguntan, debaten, se enganchan. No lo podía creer.',
    author: 'Valeria Gómez, 34 años',
    location: 'Rosario'
  }, {
    text: 'Recién empiezo a dar economía y estaba perdida con la planificación. El Mapa Anual me salvó. Supe exactamente qué dar cada mes desde el primer día.',
    author: 'Camila Torres, 27 años',
    location: 'La Plata'
  }, {
    text: 'El resolucionario es oro puro. Me ahorra horas de corrección y me da la seguridad de que estoy evaluando con criterios claros. Excelente material.',
    author: 'Javier Morales, 45 años',
    location: 'Mendoza'
  }, {
    text: 'Tomé horas de economía por primera vez este año y sentía que me faltaban herramientas. Este pack me dio la estructura y confianza que necesitaba frente al curso.',
    author: 'Lucía Fernández, 30 años',
    location: 'Tucumán'
  }, {
    text: 'Llevo más de 20 años en la docencia y sentía que mis clases se habían estancado. Este material modernizó por completo mi forma de dar la materia.',
    author: 'Marcelo Rossi, 51 años',
    location: 'Santa Fe'
  }, {
    text: 'Doy clases en una escuela de adultos y es difícil encontrar material que no sea infantil pero que sea accesible. Estos tomos tienen el equilibrio perfecto.',
    author: 'Silvina Castro, 36 años',
    location: 'Mar del Plata'
  }];
  const testimonialsPerPage = 4;
  const totalPages = Math.ceil(testimonials.length / testimonialsPerPage);
  const currentTestimonials = testimonials.slice(testimonialPage * testimonialsPerPage, (testimonialPage + 1) * testimonialsPerPage);
  const plans = [{
    id: 'starter' as const,
    icon: '📘',
    name: 'ECONOMÍA 360',
    description: '6 tomos + 1 bono',
    oldPrice: '$49.990',
    price: '$19.990',
    href: 'https://checkout.rincondigital.shop/cart/52910368882963:1'
  }, {
    id: 'completo' as const,
    icon: '🏆',
    name: 'PACK COMPLETO',
    description: '6 tomos + 4 bonos + 100 Micro-Emprendimientos Ganadores',
    oldPrice: '$69.990',
    price: '$24.990',
    badge: 'MÁS ELEGIDO',
    savings: 'AHORRÁS $45.000',
    href: 'https://checkout.rincondigital.shop/cart/52912857252115:1',
    highlighted: true
  }, {
    id: 'full360' as const,
    icon: '⭐',
    name: 'PACK FULL 360',
    description: 'Economía 360 + SIC 360 + 100 Micro-Emprendimientos',
    oldPrice: '$79.990',
    price: '$34.990',
    badgeAlt: 'MAYOR VALOR',
    href: 'https://checkout.rincondigital.shop/cart/52917897363731:1'
  }];
  const selectedPlanData = plans.find(p => p.id === selectedPlan);
  const faqs = [{
    q: '¿Qué recibo al comprar?',
    a: 'Acceso inmediato a los 6 Tomos en formato digital, con actividades, resolucionario completo y los 4 bonos según el plan elegido.'
  }, {
    q: '¿Cuándo me llega?',
    a: 'En menos de 5 minutos recibís el acceso por email.'
  }, {
    q: '¿Es físico o digital?',
    a: 'Es 100% digital. Podés descargarlo, imprimirlo o usarlo desde cualquier dispositivo.'
  }, {
    q: '¿Necesito ser docente recibido?',
    a: 'No. Está diseñado para docentes de economía en actividad, en formación o que buscan mejorar su práctica en el aula.'
  }, {
    q: '¿Tengo acceso para siempre?',
    a: 'Sí. Acceso de por vida con todas las actualizaciones incluidas.'
  }, {
    q: '¿Qué pasa si no me llega el acceso?',
    a: 'Escribinos a nuestro soporte y lo resolvemos en minutos.'
  }, {
    q: '¿Es una compra segura?',
    a: 'Sí. Pago 100% encriptado y garantía de devolución de 7 días sin preguntas.'
  }];
  return <div className="min-h-screen bg-navy-900 font-sans text-slate-200 selection:bg-gold-500/30" data-id="element-25">
      <Navbar data-id="element-26" />
      <SocialProofPopup data-id="element-27" />
      <MetaPixel data-id="element-28" />

      {/* HERO SECTION */}
      <section className="relative pt-16 pb-10 md:pt-24 md:pb-20 px-4 overflow-hidden" data-id="element-29">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-navy-700 via-navy-900 to-navy-900 -z-10" data-id="element-30"></div>

        <div className="max-w-6xl mx-auto text-center" data-id="element-31">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-32">
            <h1 className="text-3xl md:text-6xl font-heading font-black text-white mb-4 md:mb-6 leading-tight" data-id="element-33">
              Micro & Macro Economía{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-400 to-yellow-200" data-id="element-34">
                360
              </span>
            </h1>
          </motion.div>

          {/* Product mockup — now between title and subtitle */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="relative max-w-2xl mx-auto mb-6 md:mb-8 cursor-pointer" onClick={() => document.getElementById('compra')?.scrollIntoView({
          behavior: 'smooth'
        })} data-id="element-35">
            <div className="absolute inset-0 bg-gold-500/20 blur-3xl rounded-full -z-10" data-id="element-36"></div>
            <img src="https://cdn.magicpatterns.com/uploads/g7cTeWnEXXavw1mymELxGp/Cover_micro_y_macro.png" alt="Pack Micro y Macro Economía 360" className="w-full h-auto drop-shadow-2xl hover:scale-105 transition-transform duration-500" data-id="element-37" />
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.3
        }} data-id="element-38">
            <p className="text-lg md:text-2xl text-slate-300 max-w-3xl mx-auto mb-3 md:mb-4 font-medium" data-id="element-39">
              El sistema más completo de actividades, proyectos y evaluaciones
              listos para usar — con resolucionario incluido.
            </p>
            <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto mb-6 md:mb-10" data-id="element-40">
              Todo el año de Micro y Macro Economía resuelto. Sin planificar
              nada desde cero.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="flex flex-col items-center" data-id="element-41">
            <ScrollButton className="text-lg md:text-xl mb-6 md:mb-8 w-full max-w-md" data-id="element-42">
              👉 Quiero acceso inmediato
            </ScrollButton>

            <div className="flex flex-wrap justify-center gap-3 md:gap-6 text-xs md:text-sm text-slate-300 mb-8 md:mb-12" data-id="element-43">
              <span className="flex items-center gap-1.5" data-id="element-44">
                <Lock className="w-3.5 h-3.5 text-gold-400" data-id="element-45" /> Pago 100% seguro
              </span>
              <span className="flex items-center gap-1.5" data-id="element-46">
                <Zap className="w-3.5 h-3.5 text-gold-400" data-id="element-47" /> Descarga inmediata
              </span>
              <span className="flex items-center gap-1.5" data-id="element-48">
                <ShieldCheck className="w-3.5 h-3.5 text-gold-400" data-id="element-49" /> Garantía 7
                días
              </span>
              <span className="flex items-center gap-1.5" data-id="element-50">
                <Lock className="w-3.5 h-3.5 text-gold-400" data-id="element-51" /> Acceso de por
                vida
              </span>
            </div>

            <div className="max-w-4xl w-full" data-id="element-52">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 text-center mb-6 md:mb-8" data-id="element-53">
                <div data-id="element-54">
                  <div className="text-xl md:text-2xl font-bold text-white mb-0.5" data-id="element-55">
                    +1.200
                  </div>
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider" data-id="element-56">
                    docentes ya lo usan
                  </div>
                </div>
                <div data-id="element-57">
                  <div className="text-xl md:text-2xl font-bold text-white mb-0.5 flex items-center justify-center gap-1" data-id="element-58">
                    4.9/5{' '}
                    <Star className="w-3.5 h-3.5 md:w-4 md:h-4 text-gold-400 fill-gold-400" data-id="element-59" />
                  </div>
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider" data-id="element-60">
                    valoración promedio
                  </div>
                </div>
                <div data-id="element-61">
                  <div className="text-xl md:text-2xl font-bold text-white mb-0.5" data-id="element-62">
                    +6 Tomos
                  </div>
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider" data-id="element-63">
                    de contenido profesional
                  </div>
                </div>
                <div data-id="element-64">
                  <div className="text-xl md:text-2xl font-bold text-gold-400 mb-0.5" data-id="element-65">
                    #1
                  </div>
                  <div className="text-[10px] md:text-xs text-slate-400 uppercase tracking-wider" data-id="element-66">
                    Sistema elegido en Argentina
                  </div>
                </div>
              </div>
              <p className="text-xs md:text-sm text-slate-400 mb-2 md:mb-3 text-center" data-id="element-67">
                Utilizado en todo tipo de contextos:
              </p>
              <div className="flex flex-wrap justify-center gap-1.5 md:gap-2" data-id="element-68">
                {['Escuelas Públicas', 'Colegios Privados', 'Institutos Técnicos', 'Bachilleratos', 'Escuelas de Adultos', 'Educación a Distancia'].map(tag => <span key={tag} className="text-slate-400 text-[10px] md:text-xs px-2 md:px-3 py-0.5 md:py-1 rounded-full border border-navy-700" data-id="element-69">
                    {tag}
                  </span>)}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* WHAT'S INCLUDED SECTION */}
      <section className="py-10 md:py-20 px-4 bg-navy-800" data-id="element-70">
        <div className="max-w-5xl mx-auto" data-id="element-71">
          <FadeIn data-id="element-72">
            <h2 className="text-2xl md:text-5xl font-heading font-bold text-center text-white mb-3 md:mb-4" data-id="element-73">
              ¿Qué incluye el Sistema?
            </h2>
            <p className="text-center text-base md:text-xl text-slate-300 mb-8 md:mb-16 max-w-3xl mx-auto" data-id="element-74">
              6 TOMOS diseñados para cubrir cada aspecto de tu año escolar — con
              actividades y resolucionario completo
            </p>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6" data-id="element-75">
            {tomos.map((tomo, index) => <FadeIn key={tomo.id} delay={index * 0.1} data-id="element-76">
                <div className={`h-full rounded-xl md:rounded-2xl p-4 md:p-6 border backdrop-blur-sm ${tomo.color} transition-transform hover:-translate-y-1 duration-300`} data-id="element-77">
                  <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4" data-id="element-78">
                    <div className="p-2 md:p-3 bg-navy-900/50 rounded-lg md:rounded-xl" data-id="element-79">
                      {tomo.icon}
                    </div>
                    <h3 className="text-base md:text-xl font-heading font-bold text-white leading-tight" data-id="element-80">
                      {tomo.title}
                    </h3>
                  </div>
                  <p className="text-gold-400 font-medium italic mb-3 md:mb-4 text-sm md:text-base" data-id="element-81">
                    {tomo.subtitle}
                  </p>
                  <p className="text-slate-300 text-xs md:text-sm mb-4 md:mb-6 leading-relaxed" data-id="element-82">
                    {tomo.topics}
                  </p>
                  <div className="bg-navy-900/80 rounded-lg p-3 md:p-4 text-xs md:text-sm text-slate-200 border border-navy-700" data-id="element-83">
                    {tomo.activities}
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <div className="text-center mt-8 md:mt-16" data-id="element-84">
            <ScrollButton data-id="element-85">👉 Quiero los 6 Tomos ahora</ScrollButton>
          </div>
        </div>
      </section>

      {/* BONUSES SECTION */}
      <section id="bonos" className="py-10 md:py-20 px-4 bg-gradient-to-b from-navy-800 to-navy-900" data-id="element-86">
        <div className="max-w-5xl mx-auto" data-id="element-87">
          <FadeIn data-id="element-88">
            <div className="text-center mb-6 md:mb-12" data-id="element-89">
              <span className="inline-block py-1 px-4 rounded-full bg-red-500/20 text-red-400 font-bold tracking-wider mb-3 md:mb-4 border border-red-500/30 text-xs md:text-sm" data-id="element-90">
                SÓLO POR HOY
              </span>
              <h2 className="text-2xl md:text-5xl font-heading font-bold text-white mb-3 md:mb-4" data-id="element-91">
                🎁 GRATIS por el día de hoy — Bonos Exclusivos
              </h2>
            </div>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-4 md:gap-6 mb-8 md:mb-12" data-id="element-92">
            {[{
            title: 'BONO 1 — Banco de Ejemplos Reales Argentinos',
            desc: 'La economía explicada con la vida cotidiana: inflación, dólar, supermercado, tarifas. Ejemplos actualizados 2025 listos para usar en clase.'
          }, {
            title: 'BONO 2 — Clases Express para Días Caóticos',
            desc: 'Qué dar cuando no tuviste tiempo de preparar nada. Secuencias de 20 minutos, listas para aplicar. Salvavidas pedagógicos probados.'
          }, {
            title: 'BONO 3 — Evaluaciones Listas + Rúbricas Extra',
            desc: 'Variantes adicionales de evaluación para complementar el Tomo 6. Más opciones, menos trabajo.'
          }, {
            title: 'BONO 4 — Mapa Anual de Contenidos',
            desc: 'Todo el año de economía en una sola hoja. Los 6 Tomos mapeados en una secuencia ideal. Imprimilo y tenlo siempre a mano.'
          }].map((bono, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-93">
                <div className="bg-navy-800 border border-gold-500/30 rounded-xl md:rounded-2xl p-4 md:p-6 relative overflow-hidden group hover:border-gold-500 transition-colors" data-id="element-94">
                  <div className="absolute top-0 right-0 bg-gold-500 text-navy-900 text-[10px] md:text-xs font-bold px-2 md:px-3 py-0.5 md:py-1 rounded-bl-lg" data-id="element-95">
                    INCLUIDO
                  </div>
                  <h3 className="text-base md:text-xl font-bold text-gold-400 mb-2 md:mb-3 pr-16" data-id="element-96">
                    {bono.title}
                  </h3>
                  <p className="text-sm md:text-base text-slate-300" data-id="element-97">
                    {bono.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn data-id="element-98">
            <div className="text-center mt-6 md:mt-10" data-id="element-99">
              <p className="text-sm md:text-lg text-slate-400 mb-0.5" data-id="element-100">
                Valor total de los 4 bonos:{' '}
                <span className="text-slate-500 line-through" data-id="element-101">$29.990</span>
              </p>
              <p className="text-xl md:text-3xl font-black text-green-400 mb-5 md:mb-8" data-id="element-102">
                Por el día de hoy → GRATIS
              </p>
              <ScrollButton data-id="element-103">
                👉 Quiero el pack completo + los bonos
              </ScrollButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section id="testimonios" className="py-10 md:py-20 px-4 bg-navy-900" data-id="element-104">
        <div className="max-w-5xl mx-auto" data-id="element-105">
          <FadeIn data-id="element-106">
            <h2 className="text-2xl md:text-4xl font-heading font-bold text-center text-white mb-8 md:mb-12" data-id="element-107">
              Lo que dicen otros docentes
            </h2>
          </FadeIn>

          <div className="relative" data-id="element-108">
            <AnimatePresence mode="wait" data-id="element-109">
              <motion.div key={testimonialPage} initial={{
              opacity: 0,
              x: 20
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: -20
            }} transition={{
              duration: 0.3
            }} className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5" data-id="element-110">
                {currentTestimonials.map((test, i) => <div key={`${testimonialPage}-${i}`} className="bg-navy-800 p-4 md:p-6 rounded-xl border border-navy-700 flex flex-col" data-id="element-111">
                    <div className="flex gap-0.5 mb-2 md:mb-3" data-id="element-112">
                      {[...Array(5)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 md:w-4 md:h-4 fill-gold-400 text-gold-400" data-id="element-113" />)}
                    </div>
                    <p className="text-sm md:text-base text-slate-300 italic mb-3 md:mb-4 flex-grow leading-relaxed" data-id="element-114">
                      "{test.text}"
                    </p>
                    <div className="pt-3 border-t border-navy-700" data-id="element-115">
                      <p className="font-bold text-white text-sm" data-id="element-116">
                        {test.author}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-117">{test.location}</p>
                    </div>
                  </div>)}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <div className="flex items-center justify-center gap-4 mt-6 md:mt-8" data-id="element-118">
              <button onClick={() => setTestimonialPage(Math.max(0, testimonialPage - 1))} disabled={testimonialPage === 0} className="p-2 rounded-full bg-navy-800 border border-navy-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-navy-700 transition-colors" data-id="element-119">
                <ChevronLeft className="w-5 h-5 text-gold-400" data-id="element-120" />
              </button>

              <div className="flex gap-2" data-id="element-121">
                {[...Array(totalPages)].map((_, i) => <button key={i} onClick={() => setTestimonialPage(i)} className={`w-2 h-2 rounded-full transition-all ${i === testimonialPage ? 'bg-gold-400 w-6' : 'bg-navy-700 hover:bg-navy-600'}`} data-id="element-122" />)}
              </div>

              <button onClick={() => setTestimonialPage(Math.min(totalPages - 1, testimonialPage + 1))} disabled={testimonialPage === totalPages - 1} className="p-2 rounded-full bg-navy-800 border border-navy-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-navy-700 transition-colors" data-id="element-123">
                <ChevronRight className="w-5 h-5 text-gold-400" data-id="element-124" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* URGENCY BANNER — seamless between testimonials and pricing */}
      <div className="bg-gradient-to-r from-red-800 via-red-700 to-red-800 px-4 py-5 md:py-6 shadow-[0_0_30px_rgba(220,38,38,0.25)]" data-id="element-125">
        <FadeIn data-id="element-126">
          <div className="max-w-2xl mx-auto text-center" data-id="element-127">
            <p className="font-heading font-black text-white text-sm md:text-base tracking-wide mb-2 drop-shadow-md" data-id="element-128">
              🔥 PROMOCIÓN POR TIEMPO LIMITADO
            </p>
            <div className="flex items-center justify-center gap-3 mb-1.5" data-id="element-129">
              <span className="text-[11px] md:text-xs text-red-100/90" data-id="element-130">
                ⚠️ Cupos agotándose
              </span>
              <div className="w-32 md:w-44 h-2 bg-red-950/60 rounded-full overflow-hidden" data-id="element-131">
                <motion.div initial={{
                width: 0
              }} whileInView={{
                width: '80%'
              }} viewport={{
                once: true
              }} transition={{
                duration: 1.2,
                ease: 'easeOut'
              }} className="h-full rounded-full bg-gradient-to-r from-yellow-400 via-orange-400 to-yellow-300" data-id="element-132" />
              </div>
              <span className="text-[10px] md:text-xs font-bold text-yellow-300 bg-red-950/50 px-2 py-0.5 rounded-full" data-id="element-133">
                80% tomados
              </span>
            </div>
            <p className="text-[11px] md:text-xs text-red-100/70" data-id="element-134">
              Últimos cupos a precio promocional — una vez agotados, vuelve al
              precio original.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* PRICING SECTION */}
      <section id="compra" className="py-12 md:py-24 px-4 bg-navy-800 relative" data-id="element-135">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-navy-700 via-navy-800 to-navy-800 -z-10" data-id="element-136"></div>

        <div className="max-w-2xl mx-auto" data-id="element-137">
          <FadeIn data-id="element-138">
            <div className="text-center mb-8 md:mb-12" data-id="element-139">
              <h2 className="text-3xl md:text-5xl font-heading font-black text-white mb-3 md:mb-4" data-id="element-140">
                Elegí tu plan
              </h2>
              <p className="text-base md:text-lg text-slate-300" data-id="element-141">
                Dejá de perder tiempo planificando. Volvé a disfrutar el aula.
              </p>
            </div>
          </FadeIn>

          <div className="space-y-3 mb-6" data-id="element-142">
            {plans.map((plan, index) => <FadeIn key={plan.id} delay={index * 0.1} data-id="element-143">
                <button onClick={() => setSelectedPlan(plan.id)} className={`w-full text-left rounded-xl p-4 transition-all ${plan.highlighted ? 'bg-gradient-to-r from-navy-900 to-navy-800 border-2 border-gold-500' : 'bg-navy-900 border border-navy-700 hover:border-navy-600'} ${selectedPlan === plan.id ? 'ring-2 ring-gold-500 ring-offset-2 ring-offset-navy-800' : ''}`} data-id="element-144">
                  <div className="flex items-center gap-3" data-id="element-145">
                    {/* Radio button */}
                    <div className="flex-shrink-0" data-id="element-146">
                      <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selectedPlan === plan.id ? 'border-gold-500 bg-gold-500' : 'border-navy-600'}`} data-id="element-147">
                        {selectedPlan === plan.id && <div className="w-2 h-2 rounded-full bg-navy-900" data-id="element-148"></div>}
                      </div>
                    </div>

                    {/* Plan info */}
                    <div className="flex-grow min-w-0" data-id="element-149">
                      <div className="flex items-center justify-between gap-3" data-id="element-150">
                        <div className="min-w-0" data-id="element-151">
                          <h3 className="text-base md:text-lg font-black text-white flex items-center gap-2 flex-wrap" data-id="element-152">
                            {plan.icon} {plan.name}
                            {plan.badge && <span className="text-[10px] bg-gold-500 text-navy-900 px-2 py-0.5 rounded-full font-bold" data-id="element-153">
                                {plan.badge}
                              </span>}
                            {plan.badgeAlt && <span className="text-[10px] bg-blue-500 text-white px-2 py-0.5 rounded-full font-bold" data-id="element-154">
                                {plan.badgeAlt}
                              </span>}
                          </h3>
                          <p className="text-xs md:text-sm text-slate-400 mt-0.5" data-id="element-155">
                            {plan.description}
                          </p>
                          {plan.savings && <span className="inline-block mt-1.5 text-[10px] md:text-xs bg-gold-500/20 text-gold-400 px-2 py-0.5 rounded-full font-bold border border-gold-500/30" data-id="element-156">
                              {plan.savings}
                            </span>}
                        </div>

                        {/* Price */}
                        <div className="text-right flex-shrink-0" data-id="element-157">
                          <div className="text-xs text-slate-500 line-through" data-id="element-158">
                            {plan.oldPrice}
                          </div>
                          <div className="text-xl md:text-2xl font-black text-white" data-id="element-159">
                            {plan.price}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </FadeIn>)}
          </div>

          {/* CTA Button */}
          <FadeIn delay={0.4} data-id="element-160">
            <a href={selectedPlanData?.href} className="block w-full text-center bg-gold-500 hover:bg-gold-400 text-navy-900 font-black py-5 md:py-6 px-6 rounded-2xl transition-all shadow-[0_0_30px_rgba(212,160,23,0.4)] hover:shadow-[0_0_40px_rgba(212,160,23,0.6)] transform hover:-translate-y-1 text-lg md:text-xl mb-3" data-id="element-161">
              SÍ, QUIERO {selectedPlanData?.name} →
            </a>
            <p className="text-center text-sm md:text-base text-slate-400 italic mb-8" data-id="element-162">
              La opción más elegida por docentes que enseñan en los dos niveles
            </p>
          </FadeIn>

          {/* Trust badges */}
          <FadeIn delay={0.5} data-id="element-163">
            <div className="flex flex-wrap justify-center gap-4 md:gap-6 text-xs md:text-sm text-slate-300 bg-navy-900/50 rounded-xl p-4 border border-navy-700" data-id="element-164">
              <span className="flex items-center gap-1.5" data-id="element-165">
                <Lock className="w-4 h-4 text-gold-400" data-id="element-166" /> Pago seguro
              </span>
              <span className="flex items-center gap-1.5" data-id="element-167">
                <Zap className="w-4 h-4 text-gold-400" data-id="element-168" /> Acceso en 5 min
              </span>
              <span className="flex items-center gap-1.5" data-id="element-169">
                <ShieldCheck className="w-4 h-4 text-gold-400" data-id="element-170" /> Garantía 7
                días
              </span>
              <span className="flex items-center gap-1.5" data-id="element-171">
                <Download className="w-4 h-4 text-gold-400" data-id="element-172" /> Mercado Pago
              </span>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* PACK DETAILS & GUARANTEE */}
      <section className="py-10 md:py-16 px-4 bg-navy-800" data-id="element-173">
        <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8 md:gap-12 items-center" data-id="element-174">
          <FadeIn data-id="element-175">
            <div className="bg-navy-900 p-5 md:p-8 rounded-2xl md:rounded-3xl border border-navy-700" data-id="element-176">
              <h3 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6 flex items-center gap-2 md:gap-3" data-id="element-177">
                <Award className="text-gold-400 w-6 h-6 md:w-8 md:h-8" data-id="element-178" />
                {selectedPlan === 'starter' && '📘 Economía 360 incluye:'}
                {selectedPlan === 'completo' && '🏆 Pack Completo incluye:'}
                {selectedPlan === 'full360' && '⭐ Pack Full 360 incluye:'}
              </h3>
              <ul className="space-y-3 md:space-y-4" data-id="element-179">
                {(selectedPlan === 'starter' ? ['Los 6 Tomos de Micro y Macro Economía', 'Actividades progresivas y proyectos prácticos', 'Resolucionario completo con criterios de corrección', 'Bono: Mapa Anual de Contenidos', 'Acceso de por vida y actualizaciones incluidas'] : selectedPlan === 'completo' ? ['Los 6 Tomos de Micro y Macro Economía', 'Actividades progresivas y proyectos prácticos', 'Resolucionario completo con criterios de corrección', 'Bono 1: Banco de Ejemplos Reales Argentinos', 'Bono 2: Clases Express para Días Caóticos', 'Bono 3: Evaluaciones Listas + Rúbricas Extra', 'Bono 4: Mapa Anual de Contenidos', '100 Micro-Emprendimientos Ganadores — Proyecto para trabajar con alumnos de último año', 'Acceso de por vida y actualizaciones incluidas'] : ['Los 6 Tomos de Micro y Macro Economía (Economía 360)', 'SIC 360 — Sistema de Información Contable completo', '100 Micro-Emprendimientos Ganadores — Proyecto para trabajar con alumnos de último año', 'Actividades, proyectos y resolucionarios de ambos packs', 'Acceso de por vida y actualizaciones incluidas']).map((item, i) => <li key={i} className="flex items-start gap-2.5 md:gap-3" data-id="element-180">
                    <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-181" />
                    <span className="text-sm md:text-base text-slate-300" data-id="element-182">
                      {item}
                    </span>
                  </li>)}
              </ul>
            </div>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-183">
            <div className="text-center md:text-left" data-id="element-184">
              <div className="inline-flex items-center justify-center w-16 h-16 md:w-20 md:h-20 rounded-full bg-gold-500/20 mb-4 md:mb-6" data-id="element-185">
                <ShieldCheck className="w-8 h-8 md:w-10 md:h-10 text-gold-400" data-id="element-186" />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-3 md:mb-4" data-id="element-187">
                🛡️ Garantía 7 días sin riesgo
              </h3>
              <p className="text-base md:text-lg text-slate-300 leading-relaxed" data-id="element-188">
                Estamos tan seguros de la calidad de este material que te
                ofrecemos una garantía total. Si no te convence, te devolvemos
                el 100% de tu dinero. Sin preguntas.
              </p>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="py-12 md:py-24 px-4 bg-navy-800" data-id="element-189">
        <div className="max-w-3xl mx-auto" data-id="element-190">
          <FadeIn data-id="element-191">
            <h2 className="text-2xl md:text-5xl font-heading font-bold text-center text-white mb-6 md:mb-12" data-id="element-192">
              Resolvé tus dudas
            </h2>
          </FadeIn>

          <div className="space-y-3 md:space-y-4 mb-8 md:mb-16" data-id="element-193">
            {faqs.map((faq, i) => <FadeIn key={i} delay={i * 0.1} data-id="element-194">
                <div className="bg-navy-900 border border-navy-700 rounded-xl overflow-hidden" data-id="element-195">
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full px-4 md:px-6 py-3 md:py-4 text-left flex items-center justify-between focus:outline-none" data-id="element-196">
                    <span className="font-bold text-white text-base md:text-lg pr-4" data-id="element-197">
                      {faq.q}
                    </span>
                    <ChevronDown className={`w-5 h-5 text-gold-400 transition-transform duration-300 flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} data-id="element-198" />
                  </button>
                  <div className={`px-4 md:px-6 overflow-hidden transition-all duration-300 ease-in-out ${openFaq === i ? 'max-h-40 pb-3 md:pb-4 opacity-100' : 'max-h-0 opacity-0'}`} data-id="element-199">
                    <p className="text-sm md:text-base text-slate-300" data-id="element-200">
                      {faq.a}
                    </p>
                  </div>
                </div>
              </FadeIn>)}
          </div>

          <FadeIn data-id="element-201">
            <div className="text-center" data-id="element-202">
              <ScrollButton className="text-lg md:text-xl px-8 md:px-12" data-id="element-203">
                👉 Quiero acceder ahora sin riesgo
              </ScrollButton>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-navy-950 py-6 md:py-12 px-4 border-t border-navy-800" data-id="element-204">
        <div className="max-w-4xl mx-auto text-center" data-id="element-205">
          <p className="text-sm md:text-base text-slate-400 mb-1 md:mb-2" data-id="element-206">
            © 2026 Micro & Macro Economía 360 — RincónDocente360. Todos los
            derechos reservados.
          </p>
          <p className="text-xs md:text-sm text-slate-500" data-id="element-207">
            RincónDocente360 · Recursos profesionales para docentes argentinos
          </p>
        </div>
      </footer>
    </div>;
}